import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import Delete from "pages/attribute/group/delete"
import Filter from "pages/attribute/group/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useAttributeGroupRepositories from "repositories/attribute-group"
import Table from "components/table"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')

    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })

    const name = searchParams.get('name')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    const { data: attributeGroupData, isLoading: isLoadingAttributeGroupData, mutate: mutateAttributeGroupData } = useAttributeGroupRepositories({
        page,
        search,
        name,
        from,
        to,

        order_by: orderBy,
        direction
    })

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingAttributeGroupData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [attributeGroupData, isLoadingAttributeGroupData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Nama",
            sortKey: "name"
        },
        {
            isAction: false,
            content: "Is Visible",
            sortKey: "is_visible"
        },
        {
            isAction: false,
            content: "Dibuat Pada",
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: "Diubah Pada",
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: true,
            content: (value) => value.name
        },
        {
            type: "default",
            bold: true,
            content: (value) => value.is_visible ? "Yes" : "No"
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('attribute-group.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('attribute-group.delete') && (
                        <Delete data={value} onSuccess={() => mutateAttributeGroupData()} />
                    )}
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} value={searchTemp} type="text" placeholder={`${lang.search} Attribute Group`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, from, to }).filter(([_, v]) => v != null))} />
                </div>
                <div className="flex items-center space-x-2">
                    {can('attribute-group.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <Table isLoading={isLoadingAttributeGroupData} data={attributeGroupData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={attributeGroupData?.data.length === 0 && !search && !isLoadingAttributeGroupData} isEmptyOnSearching={attributeGroupData?.data.length === 0 && search && !isLoadingAttributeGroupData} toggleSort={toggleSort} orderBy={orderBy} direction={direction} />
            <Pagination links={attributeGroupData?.links} from={attributeGroupData?.from} to={attributeGroupData?.to} total={attributeGroupData?.total} />
        </div>
    )
}
